import React from 'react';

import { statFormatter } from '@nhl/formatter-utilities';

import GlobalColumnFunctions from '../../../../../../../utils/globalColumnFunctions';
import BaseColumnDefinitions, { sumValues } from '../baseColumnDefinitions';

// Shortening the function name
const CreateHeaderCell = BaseColumnDefinitions.CreateHeaderCell;

const getOvertimeShootoutIndicator = (overtime: boolean, shootout: boolean): string => {
    if (shootout) {
        return ' SO';
    }
    if (overtime) {
        return ' OT';
    }
    return '';
};

const COMEBACK_START_TIME_COL = {
    accessor: 'timeOfComebackGoal',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Comeback Start Time', 'Comeback Start Time'),
};

const MAX_DEFICIT_COL = {
    accessor: 'trailedBy',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Max Deficit', 'Max Deficit'),
};

const TEAM_SCORE_OTSO_COL = {
    accessor: 'teamScore',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Score'),
    Cell: ({ row }) => (
        <a href={`https://www.nhl.com/gamecenter/${row.original.gameId}`} target="_blank">
            {(row.original.homeRoad === 'R'
                ? row.original.team.triCode +
                  ' ' +
                  row.original.teamScore +
                  ' @ ' +
                  row.original.opponent.triCode +
                  ' ' +
                  row.original.opponentScore
                : row.original.opponent.triCode +
                  ' ' +
                  row.original.opponentScore +
                  ' @ ' +
                  row.original.team.triCode +
                  ' ' +
                  row.original.teamScore) + getOvertimeShootoutIndicator(row.original.overtime, row.original.shootout)}
        </a>
    ),
};

const COMEBACK_WINS_COL = {
    accessor: 'overallComebackWinsOnePlusGoals',
    aggregate: sumValues,
    Header: CreateHeaderCell('Comeback Wins', 'Comeback Wins'),
    getProps: GlobalColumnFunctions.getProps,
    sortDescFirst: true,
};

const TOTAL_WINS_COL = {
    accessor: 'overallComebackWinsOnePlusGoals',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Total Wins', 'Total'),
};

const ONE_GOAL_COL = {
    accessor: 'overallComebackWinsOneGoal',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('One Goal Comeback Wins', '1 Goal'),
};

const TWO_GOALS_COL = {
    accessor: 'overallComebackWinsTwoGoal',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Two Goal Comeback Wins', '2 Goals'),
};

const THREE_PLUS_GOALS_COL = {
    accessor: 'overallComebackWinsThreePlusGoals',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Three+ Goals Comeback Wins', '3+ Goals'),
};

const OPPONENT_COL = {
    Header: CreateHeaderCell('Opponent', 'Opponent', { textAlign: 'left' }),
    accessor: 'opponentName',
    minWidth: window.innerWidth > 414 ? 150 : 130,
    getProps: GlobalColumnFunctions.getProps,
    headerClassName: 'left',
    className: 'left',
};

const WIN_PCTG_COL = {
    accessor: 'overallComebackWinsPctgOfGames',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('% of Games', '% of Games'),
    Cell: ({ value }) => statFormatter.displayValueAs('shootingPctg')(value),
    sortType: 'basic',
};

const COMEBACK_WINS_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    {
        ...BaseColumnDefinitions.TEAM_COL,
        accessor: 'team.fullName',
        sortType: (a, b) => a.original.team.fullName.toUpperCase().localeCompare(b.original.team.fullName.toUpperCase()),
    },
    BaseColumnDefinitions.SEASON_COL,
    { ...BaseColumnDefinitions.GAMES_PLAYED_COL, Header: CreateHeaderCell('Total Games Played', 'Total GP') },
    { ...BaseColumnDefinitions.WINS_COL, Header: CreateHeaderCell('Total Wins', 'Total Wins') },
];
const COMEBACK_WINS_SEASON_COLS = [BaseColumnDefinitions.SEASON_COL, BaseColumnDefinitions.GAMES_PLAYED_COL];
const OVERALL_COMEBACK_WINS_COL = {
    Header: 'Overall Comeback Wins',
    headerClassName: 'top-level-header',
    columns: [TOTAL_WINS_COL, ONE_GOAL_COL, TWO_GOALS_COL, THREE_PLUS_GOALS_COL, WIN_PCTG_COL],
};
const THIRD_PERIOD_COMEBACK_WINS_COL = {
    Header: '3rd-Period Comeback Wins',
    headerClassName: 'top-level-header',
    columns: [
        { ...TOTAL_WINS_COL, accessor: 'thirdPeriodComebackWinsOnePlusGoals' },
        { ...ONE_GOAL_COL, accessor: 'thirdPeriodComebackWinsOneGoal' },
        { ...TWO_GOALS_COL, accessor: 'thirdPeriodComebackWinsTwoGoal' },
        { ...THREE_PLUS_GOALS_COL, accessor: 'thirdPeriodComebackWinsThreePlusGoal' },
        { ...WIN_PCTG_COL, accessor: 'thirdPeriodComebackWinsPctgOfGames' },
    ],
};
const COMEBACK_WINS_AFTER_TRAILING_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.TEAM_COL,
    BaseColumnDefinitions.SEASON_COL,
    BaseColumnDefinitions.GAME_DATE_COL,
    OPPONENT_COL,
    TEAM_SCORE_OTSO_COL,
    COMEBACK_START_TIME_COL,
];
export const teamComebackWinsColumnDefinitions = {
    'comeback-wins-by-season-all-teams-nhl-history': [
        ...COMEBACK_WINS_SEASON_COLS,
        OVERALL_COMEBACK_WINS_COL,
        THIRD_PERIOD_COMEBACK_WINS_COL,
    ],
    'comeback-wins-by-playoff-year-all-teams-playoff-history': [
        ...COMEBACK_WINS_SEASON_COLS,
        OVERALL_COMEBACK_WINS_COL,
        THIRD_PERIOD_COMEBACK_WINS_COL,
    ],
    'most-wins-after-trailing-by-one-goal-season': [...COMEBACK_WINS_COLS, COMEBACK_WINS_COL],
    'most-wins-after-trailing-by-two-goals-season': [
        ...COMEBACK_WINS_COLS,
        { ...COMEBACK_WINS_COL, accessor: 'overallComebackWinsTwoPlusGoals' },
    ],
    'most-wins-after-trailing-by-three-goals-season': [
        ...COMEBACK_WINS_COLS,
        { ...COMEBACK_WINS_COL, accessor: 'overallComebackWinsThreePlusGoals' },
    ],
    'most-wins-after-trailing-by-one-goal-in-third-period-season': [
        ...COMEBACK_WINS_COLS,
        { ...COMEBACK_WINS_COL, accessor: 'thirdPeriodComebackWinsOnePlusGoals' },
    ],
    'most-wins-after-trailing-by-two-goals-in-third-period-season': [
        ...COMEBACK_WINS_COLS,
        { ...COMEBACK_WINS_COL, accessor: 'thirdPeriodComebackWinsTwoPlusGoals' },
    ],
    'most-wins-after-trailing-by-three-goals-in-third-period-season': [
        ...COMEBACK_WINS_COLS,
        { ...COMEBACK_WINS_COL, accessor: 'thirdPeriodComebackWinsThreePlusGoals' },
    ],
    'most-wins-after-trailing-by-one-goal-playoff-year': [
        ...COMEBACK_WINS_COLS,
        { ...COMEBACK_WINS_COL, accessor: 'overallComebackWinsOnePlusGoals' },
    ],
    'most-wins-after-trailing-by-two-goals-playoff-year': [
        ...COMEBACK_WINS_COLS,
        { ...COMEBACK_WINS_COL, accessor: 'overallComebackWinsTwoPlusGoals' },
    ],
    'most-wins-after-trailing-by-one-goal-in-third-period-playoff-year': [
        ...COMEBACK_WINS_COLS,
        { ...COMEBACK_WINS_COL, accessor: 'thirdPeriodComebackWinsOnePlusGoals' },
    ],
    'most-wins-after-trailing-by-two-goals-in-third-period-playoff-year': [
        ...COMEBACK_WINS_COLS,
        { ...COMEBACK_WINS_COL, accessor: 'thirdPeriodComebackWinsTwoPlusGoals' },
    ],
    'wins-after-trailing-by-three-goals-all-time': [...COMEBACK_WINS_AFTER_TRAILING_COLS, MAX_DEFICIT_COL],
    'wins-after-trailing-by-three-goals-in-third-period-all-time': [
        ...COMEBACK_WINS_AFTER_TRAILING_COLS,
        { ...MAX_DEFICIT_COL, accessor: 'trailedByP3' },
    ],
    'wins-after-trailing-by-three-goals-playoff-history': [...COMEBACK_WINS_AFTER_TRAILING_COLS, MAX_DEFICIT_COL],
    'wins-after-trailing-by-three-goals-in-third-period-playoff-history': [
        ...COMEBACK_WINS_AFTER_TRAILING_COLS,
        { ...MAX_DEFICIT_COL, accessor: 'trailedByP3' },
    ],
    'wins-after-trailing-by-four-goals-all-time': [...COMEBACK_WINS_AFTER_TRAILING_COLS, MAX_DEFICIT_COL],
    'wins-after-trailing-by-four-goals-in-third-period-all-time': [
        ...COMEBACK_WINS_AFTER_TRAILING_COLS,
        { ...MAX_DEFICIT_COL, accessor: 'trailedByP3' },
    ],
};
